import { useCallback, useEffect, useMemo, useRef, useState, type MouseEvent, type PropsWithChildren } from 'react';
import { Handle, Position, useStore } from 'reactflow';
import { useTranslation } from 'react-i18next';
import { ToggleTip } from '@figma/fpl-components';
import cssVars from '@figma/fpl-tokens/css-vars';
import { color as token } from '@figma/fpl-tokens/tokens.stylex';
import { TextSmRg, TextStdMd } from '@/UI/AppTypography/AppTypography';
import { Box } from '@/UI/Box/Box';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { color } from '@/colors';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useConnectionContext } from '@/components/Recipe/FlowComponents/FlowTour/ConnectionContext';
import { HandleType } from '@/enums/handle-type.enum';
import { HandleSide } from '@/types/node';
import { useEdges, useFlowView } from '@/components/Recipe/FlowContext';
import { I18N_KEYS } from '@/language/keys';
import { useWorkflowStore, useUserWorkflowRole } from '@/state/workflow.state';
import { ZIndex } from '@/consts/z-indexes.consts';
import { useTour } from '../../ProductTours/TourContext';
import { StepKeys, TourKeys } from '../../ProductTours/tour-keys';
import { useIsCanvasReadOnlyForToolTab } from '../../Recipe/FlowComponents/WorkflowRightToolbar/toolTabReadOnly.store';
import { useObscuredNodeFilter } from './useObscuredNodeFilter';
import RequiredHandle from './RequiredHandle';
import { getHandleColor, getHandleName } from './HandlesUtils';
import { HANDLE_TRANSITION_DELAY_MS } from './handleCollapse';

const TRANSITION_DURATION = 0.2;
const TRANSITION_DELAY = HANDLE_TRANSITION_DELAY_MS / 1000;

const Circle = ({ radius, fill, style }: { radius: number; fill: string; style: React.CSSProperties }) => {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ position: 'absolute', pointerEvents: 'none', ...style }}
    >
      <circle cx="8" cy="8" r={radius} fill={fill} />
    </svg>
  );
};

const HandleLabelText = ({ children, color }: PropsWithChildren<{ color: string }>) => {
  return (
    <TextStdMd
      component="span"
      color={color}
      sx={{
        textTransform: 'capitalize',
        fontFamily: 'DM Mono',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </TextStdMd>
  );
};

const useNavigationTourGlow = (index: number, handleId: string, handleSide: HandleSide) => {
  const edges = useEdges();
  const { activeTour, getCurrentStepConfig, canMoveToNextStep } = useTour();
  const { currentConnection } = useConnectionContext();
  const showGlow = useMemo(() => {
    if (
      index !== 0 ||
      !activeTour ||
      activeTour !== TourKeys.NavigationTour ||
      getCurrentStepConfig()?.stepId !== StepKeys.NAVIGATION_TOUR.CONNECT_NODES ||
      canMoveToNextStep
    ) {
      return false;
    }
    const nodeId = handleId.split('-').slice(0, -2).join('-');
    const hasEdge = edges.some((edge) => edge.source === nodeId);
    if (hasEdge) {
      return false;
    }
    return (
      handleId.includes('prompt') &&
      ((currentConnection === undefined && handleSide === 'source') ||
        (currentConnection !== undefined && handleSide === 'target'))
    );
  }, [activeTour, canMoveToNextStep, currentConnection, edges, getCurrentStepConfig, handleId, handleSide, index]);
  return showGlow;
};

const MAX_LABEL_LENGTH = 20;

const formatLabel = (label: string): string => {
  return label.replaceAll('_', ' ');
};

const truncateLabel = (text: string): string => {
  return text.length > MAX_LABEL_LENGTH ? `${text.slice(0, MAX_LABEL_LENGTH)}...` : text;
};

const getHandleStyle = ({
  hasError,
  handleTop,
  index,
  handleSpace,
  handleBackgroundColor,
  handleBorderColor,
  role,
  obscureHandle,
  obscuredFilter,
  handlePosition,
  transitionStyle,
  isCollapseManaged,
  isCollapsed,
}: {
  hasError?: boolean;
  handleTop: string;
  index: number;
  handleSpace: number;
  handleBackgroundColor: string;
  handleBorderColor: string;
  role: 'editor' | 'guest' | null;
  obscureHandle: boolean;
  obscuredFilter: string;
  handlePosition: Position;
  transitionStyle: React.CSSProperties;
  isCollapseManaged: boolean;
  isCollapsed: boolean;
}): React.CSSProperties => {
  return {
    top: `calc(${handleTop} + ${index * handleSpace}px)`,
    backgroundColor: handleBackgroundColor,
    border: '8px solid',
    borderColor: hasError ? color.Weavy_Error : handleBorderColor,
    cursor: role === 'guest' ? 'default' : '', // Disable pointer for guests
    pointerEvents: role === 'guest' ? 'none' : 'auto',
    filter: obscureHandle ? obscuredFilter : 'none',
    left: handlePosition === Position.Left ? -16 : undefined,
    right: handlePosition === Position.Right ? -16 : undefined,
    width: '32px',
    height: '32px',
    zIndex: ZIndex.Flow.NODE_HANDLE,
    ...transitionStyle,
    // `transitionStyle` is `all TRANSITION_DURATION`s, and TRANSITION_DURATION already equals
    // COLLAPSE_TRANSITION_MS, so `top` already animates for exactly as long as useRowRemeasure
    // re-measures for, and `filter`/`border-color`/`opacity` keep animating too. Only the per-index
    // stagger needs to go: left alone, it would make handles arrive at their new row at different
    // times, stretching the window a collapse needs re-measuring past what useRowRemeasure covers.
    ...(isCollapseManaged ? { transitionDelay: '0s' } : {}),
    // Mounted and measured so its edge keeps rendering -- React Flow drops an edge entirely when it
    // names a handle it has not measured. Transparent rather than `display: none`, which would zero
    // the measured box, and non-interactive so an invisible handle cannot be grabbed under the group.
    ...(isCollapsed ? { opacity: 0, pointerEvents: 'none' as const } : {}),
  };
};

const ENTER_DELAY = 300;
const LEAVE_DELAY = 1000;

interface DynamicHandleLabelProps {
  description?: string;
  format?: string;
  handleColor?: string;
  handleId: string;
  handlePosition: Position;
  handleSpace: number;
  handleTop: string;
  handleSide: 'source' | 'target';
  handleType?: HandleType;
  index: number;
  label: string;
  required?: boolean;
  validationErrors?: {
    required?: boolean;
    requiredEmpty?: boolean;
  };
  isSelectedNode?: boolean;
  nodeId: string;
  shouldObscureHandle?: (handleType: HandleType, handleId: string, handleSide: HandleSide) => boolean;
  shouldShowHandleLabel?: (handleType: HandleType, handleId: string, nodeId: string, handleSide: HandleSide) => boolean;
  isCollapseManaged?: boolean;
  isCollapsed?: boolean;
}

const DynamicHandleLabel = ({
  description,
  handleSide,
  handleId,
  handlePosition,
  handleSpace,
  handleTop,
  handleType,
  index,
  label,
  required = false,
  validationErrors,
  isSelectedNode = false,
  nodeId,
  shouldObscureHandle,
  shouldShowHandleLabel,
  isCollapseManaged = false,
  isCollapsed = false,
}: DynamicHandleLabelProps) => {
  const { t } = useTranslation();
  const obscuredFilter = useObscuredNodeFilter();
  const isToolTabReadOnly = useIsCanvasReadOnlyForToolTab();
  const textRef = useRef<HTMLElement>(null);
  const [labelLeft, setLabelLeft] = useState(-40);
  const edges = useEdges();
  const flowView = useFlowView();
  const isFplMigrationEnabled = useFplMigrationFlag();

  const connectionNodeId = useStore((state) => state.connectionNodeId);
  const connectionHandleId = useStore((state) => state.connectionHandleId);
  const connectionHandleType = useStore((state) => state.connectionHandleType);
  const { additionalConnections } = useConnectionContext();

  const hasTempConnection = useMemo(() => {
    // Tie sibling-handle highlighting to RF's active drag (connectionNodeId). RF clears the store
    // synchronously on drag end but additionalConnections drains via a React setState, so without
    // this guard sibling handles flash for one render after the drag ends.
    if (!connectionNodeId) return false;
    if (additionalConnections.some((c) => c.handleId === handleId && c.handleSide === handleSide)) {
      return true;
    }
    return connectionHandleId === handleId && connectionHandleType === handleSide;
  }, [connectionNodeId, connectionHandleId, connectionHandleType, handleId, handleSide, additionalConnections]);

  const hasConnection = useMemo(() => {
    const isConnected = edges.some((edge) => edge.sourceHandle === handleId || edge.targetHandle === handleId);
    return isConnected;
  }, [edges, handleId]);

  // Check if there's a connection between different handle types
  const illegalTypeConnection = useMemo(() => {
    if (handleSide === 'source') {
      return false;
    }
    const inputId = getHandleName(handleId);
    return !flowView.isInputValid(nodeId, inputId);
    // eslint-disable-next-line react-hooks-js/exhaustive-deps -- edges is a dependency because we want to invalidate the memo when edges change
  }, [edges, handleId, handleSide, flowView, nodeId]);

  const role = useUserWorkflowRole();

  useEffect(() => {
    if (textRef.current) {
      const width = textRef.current.offsetWidth + 16;
      setLabelLeft(-width);
    }
  }, [label]); // Recalculate if label changes

  // navigation tour related
  const showGlow = useNavigationTourGlow(index, handleId, handleSide);

  // hide irrelevant handles when dragging a new edge
  const obscureHandle = useMemo(() => {
    if (!shouldObscureHandle) return false;
    return shouldObscureHandle(handleType || HandleType.Any, handleId, handleSide);
  }, [handleType, handleId, handleSide, shouldObscureHandle]);

  // highlight relevant handles when dragging a new edge
  const showLabel = useMemo(() => {
    if (!shouldShowHandleLabel) return false;
    if (obscureHandle) return false;
    return shouldShowHandleLabel(handleType || HandleType.Any, handleId, nodeId || '', handleSide);
  }, [handleType, handleId, nodeId, handleSide, shouldShowHandleLabel, obscureHandle]);

  const { handleBackgroundColor, handleBorderColor, transitionStyle, showLabelStyle } = useMemo(() => {
    const handleBackgroundColor = getHandleColor(handleType || HandleType.Any);
    const handleBorderColor = isFplMigrationEnabled ? cssVars.color.bg : isSelectedNode ? color.Black88 : color.Black92;

    const transitionStyle = {
      transition: `all ${TRANSITION_DURATION}s ease-in-out`,
      transitionDelay: `${index * TRANSITION_DELAY}s`,
    };
    const showLabelStyle = {
      opacity: showLabel ? 1 : 0,
      pointerEvents: showLabel ? 'auto' : 'none',
      cursor: showLabel ? 'auto' : 'default',
    };

    return { handleBackgroundColor, handleBorderColor, transitionStyle, showLabelStyle };
  }, [handleType, showLabel, index, isSelectedNode, isFplMigrationEnabled]);

  const handleDoubleClick = useCallback(
    (e: React.MouseEvent) => {
      if (role === 'guest') {
        return;
      }
      if (handleSide === 'source') {
        e.stopPropagation();
        flowView.insertRouterOnOutput(nodeId, handleId);
        return;
      }
      if (handleSide === 'target') {
        if (handleType !== HandleType.Text || hasConnection) {
          return;
        }
        e.stopPropagation();
        flowView.insertPromptOnInput(nodeId, handleId);
      }
    },
    [handleType, hasConnection, flowView, handleSide, nodeId, handleId, role],
  );

  const setInvalidConnection = useWorkflowStore((state) => state.setInvalidConnection);
  const cyclicInvalidConnection = useWorkflowStore(
    (state) => state.invalidConnection?.errorId === 'cyclic' && state.invalidConnection.targetHandle === handleId,
  );

  const outerTooltip = useMemo(() => {
    if (cyclicInvalidConnection) {
      const content = <TextSmRg>{t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_HANDLES.CYCLIC_CONNECTION)}</TextSmRg>;
      return isFplMigrationEnabled ? <ToggleTip.Content>{content}</ToggleTip.Content> : content;
    }

    const messages: string[] = [];
    if (validationErrors?.required) {
      messages.push(t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_HANDLES.REQUIRED_VALIDATION_ERROR));
    }
    if (validationErrors?.requiredEmpty) {
      messages.push(t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_HANDLES.EMPTY_REQUIRED_VALIDATION_ERROR));
    }
    if (illegalTypeConnection) {
      messages.push(t(I18N_KEYS.RECIPE_MAIN.NODES.NODE_HANDLES.ILLEGAL_TYPE_CONNECTION));
    }

    if (messages.length === 0) {
      return undefined;
    }

    const content = <TextSmRg sx={{ whiteSpace: 'pre-line' }}>{messages.join('\n')}</TextSmRg>;
    return isFplMigrationEnabled ? <ToggleTip.Content>{content}</ToggleTip.Content> : content;
  }, [
    cyclicInvalidConnection,
    illegalTypeConnection,
    isFplMigrationEnabled,
    t,
    validationErrors?.required,
    validationErrors?.requiredEmpty,
  ]);

  const labelTooltip = useMemo(() => {
    if (outerTooltip) return undefined;
    const formattedLabel = formatLabel(label);
    const isTruncated = formattedLabel.length > MAX_LABEL_LENGTH;
    if (isTruncated && description) return `${formattedLabel}\n${description}`;
    if (isTruncated) return formattedLabel;
    return description;
  }, [outerTooltip, label, description]);

  return (
    <Tooltip
      title={outerTooltip}
      placement="top"
      enterDelay={ENTER_DELAY}
      onClose={cyclicInvalidConnection ? () => setInvalidConnection(null) : undefined}
      leaveDelay={cyclicInvalidConnection ? LEAVE_DELAY : undefined}
    >
      <Handle
        type={handleSide}
        position={handlePosition}
        // A collapsed member stays mounted and measured at its group's point (see getHandleStyle),
        // so React Flow's distance-based drop fallback (getClosestHandle, connectionRadius=70 in
        // Flow.jsx) can still select it even though pointerEvents: 'none' defeats the DOM-hit-test
        // path. isConnectable={false} fails both paths' validator while leaving the handle mounted.
        // It overrides the canvas-wide `nodesConnectable`, so the read-only Tool tab is checked here too.
        isConnectable={!isCollapsed && !isToolTabReadOnly}
        style={getHandleStyle({
          hasError: validationErrors?.required || validationErrors?.requiredEmpty || illegalTypeConnection,
          handleTop,
          index,
          handleSpace,
          handleBackgroundColor,
          handleBorderColor,
          role,
          obscureHandle,
          obscuredFilter,
          handlePosition,
          transitionStyle,
          isCollapseManaged,
          isCollapsed,
        })}
        id={handleId}
        className={showGlow ? 'source-handle-glow' : ''}
        onDoubleClick={handleDoubleClick}
        // This disables click on connect - where user can click source handke and target handle separately, and a connection
        // is created. This is not very used, so was decided to disable it, in order to support double click features.
        // If we want to revive click to connect - need to not introduce a regression in double click feature.
        // Explanation: Both onMouseDown and onClick with stopPropagation were added to fix an issue with double click on handles.
        // When double clicking - the node was selected and we had an annoying flicker - where the right properties bar was opened and closed,
        // and non matching nodes for connection were blurred out.
        // At the time - this was relevant for double clicking to create a prompt node, and a router node on a source handle.

        onMouseDown={(e: MouseEvent) => e.stopPropagation()}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        {required && !hasConnection ? (
          <RequiredHandle
            size={12}
            color={validationErrors?.required ? token.textDanger : handleBorderColor}
            style={{
              position: 'absolute',
              zIndex: 2,
              pointerEvents: 'none',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
            }}
          />
        ) : (
          <Circle radius={5} fill={handleBorderColor} style={{ zIndex: 2 }} />
        )}
        {(hasConnection || hasTempConnection) && (
          <Circle radius={3} fill={handleBackgroundColor} style={{ zIndex: 3 }} />
        )}
        {handlePosition === Position.Left ? (
          <Tooltip title={labelTooltip} placement="right-start" enterDelay={800}>
            <Box
              sx={{
                position: 'relative',
                left: labelLeft,
                top: -20,
                width: 'fit-content',
                ...showLabelStyle,
                ...transitionStyle,
              }}
              ref={textRef}
            >
              <HandleLabelText
                color={
                  validationErrors?.required || validationErrors?.requiredEmpty || illegalTypeConnection
                    ? token.textDanger
                    : handleBackgroundColor
                }
              >
                {truncateLabel(formatLabel(label))}
                {required ? <sup style={{ color: 'inherit' }}>*</sup> : null}
              </HandleLabelText>
            </Box>
          </Tooltip>
        ) : (
          <Tooltip title={labelTooltip} placement="left-start" enterDelay={800}>
            <Box
              sx={{
                position: 'relative',
                left: 30,
                top: -20,
                width: 'fit-content',
                ...showLabelStyle,
                ...transitionStyle,
              }}
              ref={textRef}
            >
              <HandleLabelText color={handleBackgroundColor}>{truncateLabel(formatLabel(label))}</HandleLabelText>
            </Box>
          </Tooltip>
        )}
      </Handle>
    </Tooltip>
  );
};

export default DynamicHandleLabel;
